import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastViewport,
} from "@/components/ui/toast";
import { setSnackbarRef, type SnackbarRef } from "@/utils/snackbarUtils";

type Severity = "success" | "error" | "warning" | "info";

interface SnackbarContextValue {
  showMessage: (message: string, severity?: string | null) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  close: () => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export const useSnackbar = (): SnackbarContextValue => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

function detectSeverity(message: string): Severity {
  const lower = message.toLowerCase();
  if (lower.includes("error") || lower.includes("failed")) return "error";
  if (lower.includes("warning") || lower.includes("warn")) return "warning";
  if (lower.includes("info")) return "info";
  return "success";
}

function isSeverity(value: string): value is Severity {
  return (
    value === "success" ||
    value === "error" ||
    value === "warning" ||
    value === "info"
  );
}

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: Severity;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = useCallback((message: string, severity?: string | null) => {
    setSnackbar({
      open: true,
      message,
      severity: severity && isSeverity(severity) ? severity : detectSeverity(message),
    });
  }, []);

  const showSuccess = useCallback(
    (message: string) => showMessage(message, "success"),
    [showMessage],
  );
  const showError = useCallback(
    (message: string) => showMessage(message, "error"),
    [showMessage],
  );
  const showWarning = useCallback(
    (message: string) => showMessage(message, "warning"),
    [showMessage],
  );
  const showInfo = useCallback(
    (message: string) => showMessage(message, "info"),
    [showMessage],
  );

  const close = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const value: SnackbarContextValue = useMemo(
    () => ({ showMessage, showSuccess, showError, showWarning, showInfo, close }),
    [showMessage, showSuccess, showError, showWarning, showInfo, close],
  );

  // Legacy bridge so code outside the React tree (the axios interceptor) can show toasts.
  useEffect(() => {
    const ref: SnackbarRef = { showMessage, showSuccess, showError, showWarning, showInfo, close };
    setSnackbarRef(ref);
    return () => setSnackbarRef(null);
  }, [showMessage, showSuccess, showError, showWarning, showInfo, close]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <ToastProvider duration={6000}>
        <Toast
          variant={snackbar.severity}
          open={snackbar.open}
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <ToastDescription>{snackbar.message}</ToastDescription>
          <ToastClose />
        </Toast>
        <ToastViewport />
      </ToastProvider>
    </SnackbarContext.Provider>
  );
};
